"use client";

import Autoplay from "embla-carousel-autoplay";
import { ArrowRight } from "lucide-react";
import { useTranslations } from "next-intl";
import { useCallback, useEffect, useMemo, useState } from "react";
import { buildShippingVars, toTransitTemplateVars } from "@/features/shipping/shared/shippingVars";
import { useShippingSettings } from "@/features/shipping/ui/hooks/useShippingSettings";
import { Link } from "@/i18n/navigation";
import { Image } from "@/lib/client/components/Image";
import { placeholderImage } from "@/lib/shared/constants";
import type { CarouselApi } from "@/ui";
import {
  AspectRatio,
  Button,
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  cn,
  Skeleton,
} from "@/ui";
import { heroCarouselImageLayout } from "../../shared/hero-image-config";
import { getHeroImageAlt, normalizeHeroLink } from "../../shared/heroLinks";
import type { Hero } from "../../shared/types";
import { useHero } from "../hooks/useHero";

export interface HeroSectionProps {
  /** Server-fetched hero rows to avoid client JSON waterfall on first paint */
  initialHero?: Hero[];
}

export const HeroSection = ({ initialHero }: HeroSectionProps) => {
  const { data, isLoading } = useHero(initialHero);
  const hero = useMemo(() => data?.[0], [data]);
  const heroT = useTranslations("hero");
  const { settings } = useShippingSettings();
  const shippingVars = toTransitTemplateVars(buildShippingVars(settings));
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [count, setCount] = useState(0);
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set());

  // Reduced motion support - disable autoplay if user prefers reduced motion
  const prefersReducedMotion = useMemo(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }, []);

  const plugins = useMemo(() => {
    if (prefersReducedMotion) return [];
    return [
      Autoplay({
        delay: 6000,
        stopOnInteraction: true,
        stopOnMouseEnter: true,
      }),
    ];
  }, [prefersReducedMotion]);

  useEffect(() => {
    if (!api) return;
    setCount(api.scrollSnapList().length);
    setCurrent(api.selectedScrollSnap());
    api.on("select", () => {
      setCurrent(api.selectedScrollSnap());
    });
  }, [api]);

  const scrollTo = useCallback(
    (index: number) => {
      api?.scrollTo(index);
    },
    [api],
  );

  if (isLoading) {
    return (
      <Skeleton
        className="w-full hero-height"
        style={{ aspectRatio: "21/9" }}
      />
    );
  }

  // Use admin-editable slides from Payload CMS, or fallback to placeholder
  const slides =
    hero?.slides && hero.slides.length > 0
      ? hero.slides
      : [
          {
            id: "placeholder",
            src: placeholderImage.src,
            alt: placeholderImage.alt,
            label: null,
            title: heroT("default_headline"),
            description: heroT("default_subcopy"),
            ctaText: heroT("default_cta"),
            ctaLink: "/products",
          },
        ];

  return (
    <section className="relative w-full overflow-hidden hero-height bg-background">
      <Carousel
        orientation="horizontal"
        plugins={plugins}
        opts={{
          loop: true,
          align: "start",
        }}
        setApi={setApi}
        className="w-full h-full"
      >
        <CarouselContent className="h-full ml-0">
          {slides.map((slide, index: number) => {
            const isFirst = index === 0;
            const imageFailed = failedImages.has(slide.id);
            const imageSrc = imageFailed ? placeholderImage.src : slide.src;

            // Use admin content with translation fallbacks
            const label = slide.label || heroT("default_label");
            const title = slide.title || heroT("default_headline");
            const description = slide.description || heroT("default_subcopy");
            const ctaText = slide.ctaText || heroT("default_cta");
            const ctaLink = normalizeHeroLink(slide.ctaLink) ?? { href: "/products" };
            const Heading = isFirst ? "h1" : "h2";

            return (
              <CarouselItem
                key={slide.id}
                className="w-full h-full pl-0"
              >
                <div className="relative w-full h-full hero-content-grid">
                  {/* Text content panel - asymmetric offset for editorial feel */}
                  <div className="relative z-20 flex flex-col justify-center order-2 lg:order-1 pl-6 sm:pl-8 lg:pl-20 xl:pl-24 pr-6 sm:pr-8 lg:px-12 xl:px-16 py-8 sm:py-10 lg:py-16 lg:pb-24 hero-text-overlay">
                    <div className="max-w-md lg:max-w-lg">
                      {/* Eyebrow label - refined microcopy */}
                      {label && (
                        <span className="section-label block mb-3 animate-fade-in-up">{label}</span>
                      )}

                      {/* Headline - refined scale for luxury feel */}
                      <Heading
                        className="font-serif text-foreground animate-fade-in-up delay-200 break-words"
                        style={{
                          fontSize: "clamp(1.75rem, 3.5vw, 3rem)",
                          lineHeight: 1.15,
                          letterSpacing: "-0.01em",
                          fontWeight: 500,
                        }}
                      >
                        {title}
                      </Heading>

                      {/* Subcopy - refined, elegant */}
                      {description && (
                        <p className="heading-sub mb-8 lg:mb-10 animate-fade-in-up delay-300">
                          {description}
                        </p>
                      )}

                      {/* CTA buttons - luxury styling */}
                      <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 animate-fade-in-up delay-400">
                        {ctaText && ctaLink && (
                          <Button
                            variant="default"
                            className="gap-3 w-full sm:w-auto group"
                            asChild
                          >
                            <Link
                              href={ctaLink.href}
                              locale={ctaLink.locale}
                            >
                              {ctaText}
                              <ArrowRight className="h-4 w-4 transition-transform duration-500 group-hover:translate-x-1" />
                            </Link>
                          </Button>
                        )}
                        <Button
                          variant="link"
                          size="lg"
                          className="w-full sm:w-auto"
                          asChild
                        >
                          <Link href="/products">{heroT("secondary_cta")}</Link>
                        </Button>
                      </div>

                      {/* Trust microcopy - understated elegance */}
                      <div className="mt-8 lg:mt-12 pt-5 border-t border-border/20 animate-fade-in-up delay-500">
                        <p className="trust-text">{heroT("trust_line", shippingVars)}</p>
                      </div>
                    </div>
                  </div>

                  {/* Image panel - full-bleed, gallery presentation */}
                  <div className="relative order-1 lg:order-2 h-[50vh] sm:h-[60vh] lg:h-full lg:min-h-0 overflow-hidden">
                    <AspectRatio
                      ratio={4 / 3}
                      className="w-full h-full"
                    >
                      <Image
                        src={imageSrc}
                        alt={getHeroImageAlt(slide.alt, title, label)}
                        {...heroCarouselImageLayout}
                        priority={isFirst}
                        fetchPriority={isFirst ? "high" : "low"}
                        loading={isFirst ? "eager" : "lazy"}
                        sizes={heroCarouselImageLayout.sizes}
                        quality={heroCarouselImageLayout.quality}
                        style={{
                          objectFit: "cover",
                        }}
                        className="w-full h-full object-center transition-transform duration-[1.5s] ease-out"
                        onError={() => {
                          if (!imageFailed) {
                            setFailedImages((prev) => new Set(prev).add(slide.id));
                          }
                        }}
                      />
                      {/* Subtle vignette overlay for depth */}
                      <div className="absolute inset-0 bg-gradient-to-t from-background/10 via-transparent to-transparent pointer-events-none" />
                    </AspectRatio>
                  </div>
                </div>
              </CarouselItem>
            );
          })}
        </CarouselContent>

        {/* Refined carousel controls - only show if multiple slides */}
        {slides.length > 1 && (
          <>
            <CarouselPrevious className="carousel-arrow left-4 sm:left-6 lg:left-8 hidden sm:flex" />
            <CarouselNext className="carousel-arrow right-4 sm:right-6 lg:right-8 hidden sm:flex" />
          </>
        )}
      </Carousel>

      {/* Dot indicators - luxury minimal style */}
      {slides.length > 1 && (
        <div className="absolute bottom-6 sm:bottom-8 lg:bottom-12 left-0 right-0 z-30">
          <div className="flex items-center justify-center gap-3">
            {Array.from({ length: count }).map((_, index) => (
              <button
                key={index}
                onClick={() => scrollTo(index)}
                className={cn(
                  "h-1 border transition-all duration-500 ease-out",
                  index === current
                    ? "w-8 border-primary bg-transparent"
                    : "w-1 border-muted-foreground/40 bg-transparent hover:border-muted-foreground/60",
                )}
                aria-label={heroT("go_to_slide", { number: index + 1 })}
                aria-current={index === current ? "true" : "false"}
              />
            ))}
          </div>
        </div>
      )}
    </section>
  );
};
